/**
 * Sprint 4 — Agent memory hook (server-side).
 *
 * Migrated from localStorage to Supabase `agent_conversations` + `agent_messages` tables.
 * Keeps the same public API so all callers need no changes.
 *
 * Sprint 8: replaced `as any` casts with proper DB row interfaces derived
 * from the agent_conversations (10 cols) + agent_messages (12 cols) table schemas.
 */
import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import {
  fetchAgentConversations,
  fetchAgentMessages,
  createAgentConversation,
  createAgentMessage,
  type AgentConversationDto,
} from '@/integrations/api/agent_data_api';
import type { AgentMessage } from '@/agent/adapters/types';

const APPROX_CHARS_PER_TOKEN = 4;

type AgentConversationRow = AgentConversationDto;

export interface AgentConversation {
  id: string;
  orgId: string;
  userId: string;
  title: string;
  createdAt: number;
  messages: AgentMessage[];
}

// ── Query keys ────────────────────────────────────────────────────────────────
const convKey = (tenantId: string, userId: string) =>
  ['agent_conversations', tenantId, userId] as const;
const msgKey = (conversationId: string) =>
  ['agent_messages', conversationId] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────
function rowToConv(
  row: AgentConversationRow,
  messages: AgentMessage[] = []
): AgentConversation {
  return {
    id: row.id,
    orgId: row.tenant_id,
    userId: row.user_id,
    title: row.title,
    createdAt: new Date(row.started_at).getTime(),
    messages,
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useAgentMemory(opts?: { userId?: string; conversationId?: string }) {
  const { currentOrg } = useTenant();
  const tenantId = currentOrg?.id ?? '';
  // Only query Supabase when tenantId is a real UUID (not a dev mock like "org-acme")
  const isRealTenant = UUID_RE.test(tenantId);
  const userId = opts?.userId ?? 'demo-user';
  const qc = useQueryClient();

  const [activeId, setActiveId] = useState<string>(
    () => opts?.conversationId ?? `conv-${Date.now()}`
  );

  // ── list all conversations for this tenant/user ──
  const { data: conversations = [] } = useQuery<AgentConversation[]>({
    queryKey: convKey(tenantId, userId),
    // Skip query for dev mock tenants (not real UUIDs) to avoid Supabase FK errors
    enabled: isRealTenant,
    queryFn: async (): Promise<AgentConversation[]> => {
      const data = await fetchAgentConversations(userId, tenantId);
      return data.map((r) => rowToConv(r));
    },
  });

  // ── messages for the active conversation ──
  const { data: activeMessages = [] } = useQuery<AgentMessage[]>({
    queryKey: msgKey(activeId),
    enabled: isRealTenant && activeId.length > 0,
    queryFn: async (): Promise<AgentMessage[]> => {
      if (activeId.startsWith('conv-')) return [];
      const data = await fetchAgentMessages(activeId, tenantId);
      return data.map((r) => ({
        role: r.role,
        content: r.content,
        ts: new Date(r.created_at).getTime(),
      } as AgentMessage));
    },
  });

  // ── add a message ──
  const addMessageMutation = useMutation({
    mutationFn: async (msg: Omit<AgentMessage, 'ts'> & { ts?: number }) => {
      if (!tenantId) return;
      const message: AgentMessage = { ...msg, ts: msg.ts ?? Date.now() };

      const isNew = activeId.startsWith('conv-');
      if (isNew) {
        const title =
          msg.role === 'user'
            ? String(msg.content ?? '').slice(0, 80)
            : 'New conversation';
        const conv = await createAgentConversation({ user_id: userId, title }, tenantId);
        setActiveId(conv.id);
        await createAgentMessage(conv.id, { role: message.role, content: message.content ?? '' }, tenantId);
        return;
      }

      await createAgentMessage(activeId, { role: message.role, content: message.content ?? '' }, tenantId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: convKey(tenantId, userId) });
      qc.invalidateQueries({ queryKey: msgKey(activeId) });
    },
  });

  const addMessage = useCallback(
    (msg: Omit<AgentMessage, 'ts'> & { ts?: number }) => {
      addMessageMutation.mutate(msg);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addMessageMutation.mutate]
  );

  const clearHistory = useCallback(() => {
    setActiveId(`conv-${Date.now()}`);
  }, []);

  const startNewConversation = useCallback(() => {
    setActiveId(`conv-${Date.now()}`);
  }, []);

  const getContextMessages = useCallback(
    (maxTokens = 2000): AgentMessage[] => {
      const limitChars = maxTokens * APPROX_CHARS_PER_TOKEN;
      const out: AgentMessage[] = [];
      let total = 0;
      for (let i = activeMessages.length - 1; i >= 0; i--) {
        const m = activeMessages[i];
        const size = (m.content?.length ?? 0) + 8;
        if (total + size > limitChars) break;
        out.unshift(m);
        total += size;
      }
      return out;
    },
    [activeMessages]
  );

  return {
    conversations,
    activeId,
    addMessage,
    clearHistory,
    startNewConversation,
    getContextMessages,
    isLoading: addMessageMutation.isPending,
  };
}
